import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

// ───────────────────────── constants ─────────────────────────
export const BANK_TYPE = 'AXIS';

// payment modes that show up as the first segment of an Axis narration
const KNOWN_MODES = [
  'NEFT',
  'RTGS',
  'IMPS',
  'UPI',
  'IFT',
  'CLG',
  'ACH',
  'ECS',
  'CHQ',
  'CHEQUE',
  'CASH',
  'POS',
  'ATM',
  'TRF',
  'TRFR',
  'INT',
  'MB',
  'BRN',
];

// segments that sit between the mode and the real bank reference
const NOISE_SEGMENTS = new Set(['P2A', 'P2M', 'P2P', 'ATTN', 'INB', 'MB', 'BRN', 'TXN', 'IMPSTXN']);

// header aliases, so a slightly different export still lines up
const COLUMN_PATTERNS = {
  trxDate: /transaction\s*date|txn\s*date|^date$/i,
  valueDate: /value\s*date/i,
  particulars: /particular|narration|description|remark/i,
  debit: /debit/i,
  credit: /credit/i,
  balance: /balance/i,
  cheque: /cheque|chq/i,
};

// ───────────────────────── parsing helpers ─────────────────────────
const cellText = (v) => {
  if (v === null || v === undefined) return '';
  // Excel text cells often arrive with a leading apostrophe ('01/08/2026)
  return String(v).replace(/^'+/, '').trim();
};

const parseAmount = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const cleaned = cellText(v).replace(/[₹,\s]/g, '');
  if (!cleaned || !/\d/.test(cleaned)) return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
};

const pad2 = (n) => String(n).padStart(2, '0');
const isoDate = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;

// statements carry dd/mm/yyyy text, real Date cells, or Excel serial numbers
const parseStatementDate = (v) => {
  if (v === null || v === undefined || v === '') return null;

  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return isoDate(v.getFullYear(), v.getMonth() + 1, v.getDate());
  }

  if (typeof v === 'number' && Number.isFinite(v)) {
    // Excel serial day 1 is 1900-01-01, with the classic 1900 leap-year bug
    const ms = Date.UTC(1899, 11, 30) + Math.round(v) * 86400000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return isoDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
  }

  const s = cellText(v);
  if (!s) return null;

  // yyyy-mm-dd (or yyyy/mm/dd)
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) {
    const [, y, mo, d] = m;
    return Number(mo) >= 1 && Number(mo) <= 12 && Number(d) >= 1 && Number(d) <= 31 ? isoDate(y, mo, d) : null;
  }

  // dd/mm/yyyy — the documented format of this statement
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
  if (m) {
    const d = Number(m[1]);
    const mo = Number(m[2]);
    let y = Number(m[3]);
    if (y < 100) y += y > 50 ? 1900 : 2000;
    if (d < 1 || d > 31 || mo < 1 || mo > 12) return null;
    return isoDate(y, mo, d);
  }

  // dd-Mon-yyyy
  m = s.match(/^(\d{1,2})[-\s]([A-Za-z]{3,})[-\s](\d{2,4})/);
  if (m) {
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const mo = months.indexOf(m[2].slice(0, 3).toLowerCase()) + 1;
    let y = Number(m[3]);
    if (y < 100) y += y > 50 ? 1900 : 2000;
    if (!mo) return null;
    return isoDate(y, mo, Number(m[1]));
  }

  return null;
};

// mode = the leading narration segment when it is a known one, else the first
// known mode keyword anywhere in the text
export const detectMode = (particulars) => {
  const s = cellText(particulars).toUpperCase();
  if (!s) return '';
  const first = s.split(/[/\-\s]/)[0].trim();
  if (KNOWN_MODES.includes(first)) return first === 'TRFR' ? 'TRF' : first === 'CHEQUE' ? 'CHQ' : first;
  const hit = KNOWN_MODES.find((m) => new RegExp(`(^|[^A-Z])${m}([^A-Z]|$)`).test(s));
  if (hit) return hit === 'TRFR' ? 'TRF' : hit === 'CHEQUE' ? 'CHQ' : hit;
  return first && /^[A-Z]{2,10}$/.test(first) ? first : 'OTHER';
};

// the bank reference is the first narration segment that carries digits and is
// long enough to be an id — skipping the mode and the P2A/ATTN style noise
export const extractReference = (particulars) => {
  const s = cellText(particulars);
  if (!s) return '';
  const segments = s
    .split('/')
    .map((x) => x.trim())
    .filter(Boolean);
  const candidates = segments.slice(1).filter((seg) => {
    const up = seg.toUpperCase();
    if (NOISE_SEGMENTS.has(up)) return false;
    if (!/\d/.test(seg)) return false;
    if (seg.includes(' ')) return false; // names and free text, not a reference
    return seg.length >= 5;
  });
  const ref = candidates[0] || '';
  return ref.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
};

/** The 1st of the current month, as YYYY-MM-DD. */
export const currentMonthStart = (today = new Date()) =>
  `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

/** 2026-09-25 -> 250926, the tail of a MOB transaction id. */
export const ddmmyy = (trxDate) => {
  const m = (trxDate ?? '').toString().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}${m[2]}${m[1].slice(2)}` : '';
};

/**
 * The transaction id: AXIS + the reference in the narration.
 *
 * MOB is the exception. Its "reference" is the payer's ACCOUNT number, which is
 * identical on every payment that account ever makes — so two different payments
 * a month apart both produced AXIS921020010842852, the second was read as one
 * already loaded, and it could never be added. The date makes a MOB id belong to
 * its own payment: AXIS + account + ddmmyy.
 */
export const buildTrxId = (particulars, trxDate) => {
  const ref = extractReference(particulars);
  if (!ref) return '';
  const base = `${BANK_TYPE}${ref}`;
  return detectMode(particulars) === 'MOB' ? `${base}${ddmmyy(trxDate)}` : base;
};

// Narration reduced to letters and digits: punctuation and spacing differences
// between two exports of the same statement must not change the outcome.
export const normalizedNarration = (description) =>
  (description ?? '').toString().toUpperCase().replace(/[^A-Z0-9]/g, '');

// Short, stable hash (FNV-1a) rendered base36 — same input always gives the same
// value, which is what makes a re-upload produce the same ids.
const shortHash = (text) => {
  let h = 0x811c9dc5;
  const s = (text ?? '').toString();
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36).toUpperCase().padStart(7, '0');
};

/**
 * Fallback id for a credit whose narration has no reference number.
 * Built from the date, the narration and the amount, so the same statement row
 * always yields the same id — no sheet position involved. That makes a repeat
 * upload recognisable instead of creating a second payment.
 */
export const fallbackTrxId = (trxDate, description, amount) => {
  const day = (trxDate ?? '').toString().replace(/-/g, '');
  const narration = normalizedNarration(description);
  const paise = Math.round(Math.abs(Number(amount) || 0) * 100);
  return `${BANK_TYPE}${day}X${shortHash(`${narration}|${paise}`)}`;
};

/**
 * Turn a sheet into upload-ready rows.
 * Keeps only rows that have a transaction date AND a credit amount — debits are
 * money out and are not collections, so they are dropped.
 */
export const parseStatementRows = (rowsAoA) => {
  // locate the header row by looking for the transaction-date column
  const headerIdx = rowsAoA.findIndex((row) =>
    (row || []).some((cell) => COLUMN_PATTERNS.trxDate.test(cellText(cell)))
  );
  if (headerIdx === -1) {
    return { error: 'Could not find a "Transaction Date" column — is this an Axis bank statement export?' };
  }

  const header = (rowsAoA[headerIdx] || []).map((c) => cellText(c));
  const colOf = (pattern) => header.findIndex((h) => pattern.test(h));
  const idx = {
    trxDate: colOf(COLUMN_PATTERNS.trxDate),
    valueDate: colOf(COLUMN_PATTERNS.valueDate),
    particulars: colOf(COLUMN_PATTERNS.particulars),
    debit: colOf(COLUMN_PATTERNS.debit),
    credit: colOf(COLUMN_PATTERNS.credit),
    balance: colOf(COLUMN_PATTERNS.balance),
    cheque: colOf(COLUMN_PATTERNS.cheque),
  };

  if (idx.particulars === -1) return { error: 'Could not find a "Particulars" column in the file.' };
  if (idx.credit === -1) return { error: 'Could not find a "Credit Amount" column in the file.' };

  const rows = [];
  const skipped = { noDate: 0, debit: 0, badDate: 0, noAmount: 0 };
  let older = 0; // imported, just reported separately
  const monthStart = currentMonthStart();
  const seen = new Map(); // trx_id -> how many times used, to keep ids unique
  const seenSignatures = new Set(); // date+narration+amount, to spot true repeats
  let duplicates = 0;
  let missingRef = 0;
  let sharedRefs = 0;

  rowsAoA.slice(headerIdx + 1).forEach((raw, i) => {
    const row = raw || [];
    const rawDate = row[idx.trxDate];
    if (cellText(rawDate) === '') {
      skipped.noDate += 1; // opening balance, totals, blank spacer rows
      return;
    }

    const trx_date = parseStatementDate(rawDate);
    if (!trx_date) {
      skipped.badDate += 1;
      return;
    }

    const credit = parseAmount(row[idx.credit]);
    const debit = idx.debit === -1 ? null : parseAmount(row[idx.debit]);
    if (credit === null || credit === 0) {
      if (debit !== null && debit !== 0) skipped.debit += 1;
      else skipped.noAmount += 1;
      return;
    }

    // Statements are often re-uploaded, so earlier months used to be dropped
    // here. They are imported now — a month-end credit only shows up in the
    // statement pulled the following month, and was otherwise unreachable.
    // Re-uploads stay safe because the transaction id dedupes them.
    if (trx_date < monthStart) older += 1;

    const trx_description = cellText(row[idx.particulars]);
    let trx_id = buildTrxId(trx_description, trx_date);
    if (!trx_id) {
      // no reference in the narration — derive the id from date + narration +
      // amount instead, so it is identical every time this row is uploaded
      missingRef += 1;
      trx_id = fallbackTrxId(trx_date, trx_description, credit);
    }

    // Two tests, and they are not the same thing:
    //  · same date + narration + amount  -> the same credit listed twice, skip it
    //  · same id, different credit       -> the "reference" is really an account
    //    number (MOB/TPFT/.../921020010842852 repeats on every payment from that
    //    account), so this is a different payment that needs a distinct id
    const signature = `${trx_date}|${normalizedNarration(trx_description)}|${Math.round(credit * 100)}`;
    const isDuplicate = seenSignatures.has(signature);
    seenSignatures.add(signature);
    if (isDuplicate) duplicates += 1;

    const idUses = seen.get(trx_id) || 0;
    seen.set(trx_id, idUses + 1);
    if (!isDuplicate && idUses > 0) {
      trx_id = `${trx_id}-${idUses + 1}`; // keeps a real payment from being merged away
      sharedRefs += 1;
    }

    rows.push({
      trx_id,
      trx_date,
      trx_description,
      trx_mode: detectMode(trx_description),
      trx_amt: credit,
      bank_type: BANK_TYPE,
      _sheetRow: headerIdx + 2 + i, // 1-based, for the preview only
      _duplicate: isDuplicate,
      _valueDate: idx.valueDate === -1 ? '' : cellText(row[idx.valueDate]),
    });
  });

  return { rows, skipped, duplicates, missingRef, sharedRefs, older };
};

// Fixed layout: the description takes whatever the other columns do not need.
const PREVIEW_COLS = [
  { label: 'Row', width: 46 },
  { label: 'Transaction ID', width: 186 },
  { label: 'Date', width: 96 },
  { label: 'Mode', width: 66, align: 'center' },
  { label: 'Amount', width: 108, align: 'right' },
  { label: 'Description', width: 'auto' },
];

const CELL = { fontSize: '11px', padding: '5px 8px', borderBottom: '1px solid #f1f5f9' };

// the payload keeps ISO; people read dd Mon yyyy
const displayDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mi = Number(m) - 1;
  return months[mi] ? `${d} ${months[mi]} ${y}` : iso;
};

// One cell of the summary strip.
function Summary({ label, value, hint, color, grow, last }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        flex: grow ? '1 1 130px' : '0 0 auto',
        minWidth: 110,
        borderRight: last ? 'none' : '1px solid #eef2f7',
      }}
    >
      <Typography sx={{ fontSize: '9px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '17px', fontWeight: 700, color, lineHeight: 1.15 }}>{value}</Typography>
        {hint && <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>{hint}</Typography>}
      </Box>
    </Box>
  );
}

// ───────────────────────── component ─────────────────────────
function BankStatementUpload({ open, onClose, email, onUploaded, existingTrxIds, existingRange }) {
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null); // { rows, skipped, duplicates, missingRef }
  // trx_ids of payments outside the window this screen has loaded. A file can
  // carry earlier months, and those would otherwise be checked against nothing.
  const [outsideIds, setOutsideIds] = useState(null); // null = not needed / not done yet
  const [checkingOutside, setCheckingOutside] = useState(false);
  const [outsideError, setOutsideError] = useState(null);
  const [outsideRange, setOutsideRange] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFileName('');
    setParsed(null);
    setError(null);
    setResult(null);
    setParsing(false);
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const readFile = (file) => {
    if (!file) return;
    reset();
    setFileName(file.name);
    setParsing(true);
    const reader = new FileReader();
    reader.onerror = () => {
      setParsing(false);
      setError('Could not read that file.');
    };
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: '', blankrows: false });
        const out = parseStatementRows(aoa);
        if (out.error) setError(out.error);
        else setParsed(out);
      } catch (err) {
        setError(err?.message || 'That file could not be parsed as a spreadsheet.');
      } finally {
        setParsing(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // The file's own span. Rows are already date-filtered, so min/max is enough.
  const fileRange = useMemo(() => {
    const dates = (parsed?.rows || []).map((r) => r.trx_date).filter(Boolean).sort();
    return dates.length ? { from: dates[0], to: dates[dates.length - 1] } : null;
  }, [parsed]);

  // Anything in the file that predates (or postdates) what this screen loaded has
  // not been checked against the payments table at all. Fetch exactly that span
  // and fold its trx_ids in, otherwise a re-uploaded old statement duplicates.
  useEffect(() => {
    const loadedFrom = existingRange?.from;
    const loadedTo = existingRange?.to;
    if (!fileRange || !loadedFrom || !loadedTo) return;
    const needFrom = fileRange.from < loadedFrom ? fileRange.from : null;
    const needTo = fileRange.to > loadedTo ? fileRange.to : null;
    if (!needFrom && !needTo) {
      setOutsideIds(null);
      setOutsideError(null);
      setOutsideRange(null);
      return; // the loaded window already covers the file
    }
    const from = needFrom || loadedFrom;
    const to = needTo || loadedTo;
    const api = process.env.REACT_APP_CLIENT_PAYMENT_DETAILS;
    if (!api) {
      setOutsideError('Cannot verify older transactions — REACT_APP_CLIENT_PAYMENT_DETAILS is not configured.');
      return;
    }
    let cancelled = false;
    setCheckingOutside(true);
    setOutsideError(null);
    setOutsideRange({ from, to });
    axios
      .get(api, { params: { start_date: from, end_date: to } })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data ?? [];
        const set = new Set();
        (Array.isArray(data) ? data : []).forEach((r) => {
          const id = r.trx_id ?? r.id ?? r.txn_id;
          if (id != null && id !== '') set.add(String(id).trim().toUpperCase());
        });
        setOutsideIds(set);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Could not load payments for the file date range:', err);
        setOutsideIds(null);
        setOutsideError('Could not check these dates against existing payments — uploading now risks duplicates.');
      })
      .finally(() => {
        if (!cancelled) setCheckingOutside(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fileRange, existingRange?.from, existingRange?.to]);

  // mark rows whose trx_id is already in the payments table — those would be a
  // second copy of a payment that is already in the system
  const marked = useMemo(() => {
    if (!parsed?.rows) return [];
    const loaded = existingTrxIds instanceof Set ? existingTrxIds : new Set();
    // the window this screen loaded, plus whatever the file reaches outside it
    const known = outsideIds ? new Set([...loaded, ...outsideIds]) : loaded;
    return parsed.rows.map((r) => ({
      ...r,
      _exists: known.has(String(r.trx_id).trim().toUpperCase()),
    }));
  }, [parsed, existingTrxIds, outsideIds]);

  // neither an already-uploaded payment nor a repeat of a row above it is sent
  const freshRows = useMemo(() => marked.filter((r) => !r._exists && !r._duplicate), [marked]);
  const alreadyCount = marked.filter((r) => r._exists).length;
  const dupCount = marked.filter((r) => r._duplicate && !r._exists).length;

  const handleUpload = async () => {
    const api = process.env.REACT_APP_UPLOAD_BANK_STATEMENT;
    if (!api) {
      setError('API not configured (REACT_APP_UPLOAD_BANK_STATEMENT)');
      return;
    }
    if (!freshRows.length) return;
    setUploading(true);
    setError(null);
    try {
      // strip the preview-only fields before sending; rows already in payments
      // are left out so the upload cannot duplicate them
      const payload = freshRows.map(({ _sheetRow, _duplicate, _valueDate, _exists, ...row }) => ({
        ...row,
        uploaded_by: email ?? '',
      }));
      const res = await axios.post(api, payload);
      const inserted = res?.data?.inserted ?? res?.data?.count;
      setResult({
        ok: true,
        message:
          typeof inserted === 'number'
            ? `${inserted} of ${payload.length} transactions saved.`
            : `${payload.length} transaction${payload.length === 1 ? '' : 's'} sent successfully.`,
      });
      if (onUploaded) onUploaded(payload.length);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const totals = useMemo(
    () => ({
      count: freshRows.length,
      amount: freshRows.reduce((s, r) => s + (r.trx_amt || 0), 0),
    }),
    [freshRows]
  );

  const skippedTotal = parsed
    ? parsed.skipped.noDate + parsed.skipped.debit + parsed.skipped.badDate + parsed.skipped.noAmount
    : 0;

  const money = (n) =>
    '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', width: '92vw', maxWidth: 1150 } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
            color: '#fff',
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <AccountBalanceIcon sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.2 }}>
              Upload Bank Statement
            </Typography>
            <Typography sx={{ fontSize: '11px', opacity: 0.9 }}>
              Axis Bank statement (.xlsx, .xls or .csv) — credit entries are imported as payments
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={uploading} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pt: 1.5, pb: 1.5, backgroundColor: '#f8faff' }}>
        {/* Full drop zone only while there is nothing loaded; once a file is
            parsed it collapses to a one-line bar so the table gets the room. */}
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          hidden
          onChange={(e) => {
            readFile(e.target.files?.[0]);
            e.target.value = ''; // let the same file be picked again
          }}
        />

        {parsed && !parsing ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid #e2e8f0',
              backgroundColor: '#fff',
              borderRadius: '8px',
              px: 1.25,
              py: 0.75,
              mb: 1,
            }}
          >
            <UploadFileIcon sx={{ fontSize: 18, color: '#1e40af', flexShrink: 0 }} />
            <Typography
              sx={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={fileName}
            >
              {fileName}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {marked.length + skippedTotal} rows read
            </Typography>
            <Button
              size="small"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              sx={{ ml: 'auto', textTransform: 'none', fontSize: '11px', color: '#1e40af', flexShrink: 0 }}
            >
              Change file
            </Button>
          </Box>
        ) : (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              readFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => inputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragging ? '#1e40af' : '#c7d2fe'}`,
              backgroundColor: dragging ? '#e0e7ff' : '#fff',
              borderRadius: '10px',
              py: 3,
              px: 2,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: '#1e40af', backgroundColor: '#f5f8ff' },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 30, color: '#1e40af', mb: 0.5 }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
              {fileName || 'Drop the statement here, or click to choose a file'}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#64748b', mt: 0.25 }}>
              Rows without a Transaction Date are ignored. Debit (money out) rows are skipped, as are
              credits dated before the current month.
            </Typography>
          </Box>
        )}

        {parsing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: '12px', color: '#475569' }}>Reading the statement…</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1.5, fontSize: '12px' }}>
            {error}
          </Alert>
        )}

        {result?.ok && (
          <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />} sx={{ mt: 1.5, fontSize: '12px' }}>
            {result.message}
          </Alert>
        )}

        {parsed && !parsing && (
          <>
            {/* One summary strip instead of five cards: the counts read left to
                right as the file's story, and the table keeps the height. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                flexWrap: 'wrap',
                border: '1px solid #e2e8f0',
                backgroundColor: '#fff',
                borderRadius: '8px',
                overflow: 'hidden',
                mb: 1,
              }}
            >
              <Summary
                label="Ready to upload"
                value={totals.count}
                hint={
                  parsed.older > 0
                    ? `${money(totals.amount)} · incl. ${parsed.older} from earlier months`
                    : money(totals.amount)
                }
                color="#16a34a"
                grow
              />
              <Summary
                label="Already uploaded"
                value={alreadyCount}
                hint={alreadyCount > 0 ? 'skipped' : 'none yet'}
                color={alreadyCount > 0 ? '#d97706' : '#94a3b8'}
              />
              <Summary
                label="Skipped"
                value={skippedTotal}
                hint={[
                  parsed.skipped.debit ? `${parsed.skipped.debit} debit` : '',
                  parsed.skipped.noDate ? `${parsed.skipped.noDate} no date` : '',
                  parsed.skipped.badDate ? `${parsed.skipped.badDate} bad date` : '',
                  parsed.skipped.noAmount ? `${parsed.skipped.noAmount} no amount` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
                color="#475569"
                grow
              />
              {dupCount > 0 && (
                <Summary
                  label="Duplicate in file"
                  value={dupCount}
                  hint="same date, narration and amount"
                  color="#c2410c"
                  grow
                />
              )}
              <Summary label="Bank" value={BANK_TYPE} hint="every row tagged" color="#1e40af" last />
            </Box>

            {/* the file reaches outside the window this screen loaded, so those
                dates are being checked against payments separately */}
            {checkingOutside && (
              <Alert severity="info" sx={{ mb: 1, fontSize: '12px' }}>
                Checking {outsideRange?.from} to {outsideRange?.to} against existing payments — this file
                contains dates outside the range currently loaded.
              </Alert>
            )}
            {outsideError && (
              <Alert severity="error" sx={{ mb: 1, fontSize: '12px' }}>
                {outsideError} Change the date filter on the payments screen to cover{' '}
                {fileRange?.from} to {fileRange?.to} and reopen this dialog.
              </Alert>
            )}

            {alreadyCount > 0 && (
              <Alert severity={freshRows.length === 0 ? 'success' : 'info'} sx={{ mb: 1, fontSize: '12px' }}>
                {freshRows.length === 0
                  ? `All ${marked.length} transaction${marked.length === 1 ? '' : 's'} in this file are already in payments — nothing new to upload.`
                  : `${alreadyCount} of ${marked.length} transactions already exist in payments and will be skipped — only the ${freshRows.length} new one${freshRows.length === 1 ? '' : 's'} will be uploaded.`}
                {existingRange?.from && existingRange?.to
                  ? ` (Checked against payments for ${existingRange.from} to ${existingRange.to}${
                      outsideRange ? `, plus ${outsideRange.from} to ${outsideRange.to} loaded for this file` : ''
                    }.)`
                  : ''}
              </Alert>
            )}

            {parsed.rows.length === 0 ? (
              <Alert severity="warning" icon={<ErrorOutlineIcon fontSize="small" />} sx={{ fontSize: '12px' }}>
                No credit transactions found in this file. Only rows with both a Transaction Date and a Credit
                Amount are imported.
              </Alert>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', borderRadius: '8px', maxHeight: '52vh' }}
              >
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      {PREVIEW_COLS.map((c) => (
                        <TableCell
                          key={c.label}
                          sx={{
                            backgroundColor: '#1e40af',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '10.5px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            padding: '7px 8px',
                            textAlign: c.align || 'left',
                            width: c.width,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {marked.map((r, i) => (
                      <TableRow
                        key={`${r.trx_id}-${i}`}
                        sx={{ backgroundColor: r._exists || r._duplicate ? '#fffbeb' : i % 2 === 0 ? '#fff' : '#f8faff' }}
                      >
                        <TableCell sx={{ ...CELL, color: '#cbd5e1', fontSize: '10px' }}>{r._sheetRow}</TableCell>
                        <TableCell sx={{ ...CELL, fontFamily: 'monospace', fontWeight: 600, color: r._exists ? '#b45309' : '#0059B2' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.trx_id}>
                              {r.trx_id}
                            </Box>
                            {r._exists && (
                              <Chip
                                size="small"
                                label="uploaded"
                                title="Already in the payments table — it will not be uploaded again"
                                sx={{ height: 15, fontSize: '8px', fontWeight: 700, backgroundColor: '#fff', color: '#b45309', border: '1px solid #fde68a', flexShrink: 0, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            )}
                            {r._duplicate && (
                              <Chip
                                size="small"
                                label="duplicate"
                                title="An earlier row in this file has the same date, narration and amount — this one is not uploaded"
                                sx={{ height: 15, fontSize: '8px', backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', flexShrink: 0, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...CELL, color: '#475569', whiteSpace: 'nowrap' }}>{displayDate(r.trx_date)}</TableCell>
                        <TableCell sx={{ ...CELL, textAlign: 'center' }}>
                          <Chip
                            size="small"
                            label={r.trx_mode || '—'}
                            sx={{ backgroundColor: '#eef2ff', color: '#4338ca', fontSize: '9.5px', height: 18, fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...CELL, textAlign: 'right', fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {money(r.trx_amt)}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...CELL,
                            fontSize: '10px',
                            color: '#64748b',
                            fontFamily: 'monospace',
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                          }}
                          title={r.trx_description}
                        >
                          {r.trx_description}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: '10px', color: '#94a3b8', flex: 1 }}>
          Transaction ID is AXIS + the reference in Particulars — and for MOB, which repeats the payer's
          account number on every payment, AXIS + account + ddmmyy.
          {marked.length > 0 ? ` Showing all ${marked.length} credit rows.` : ''}
        </Typography>
        <Button onClick={handleClose} disabled={uploading} sx={{ textTransform: 'none', color: '#64748b' }}>
          {result?.ok ? 'Done' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          disabled={uploading || freshRows.length === 0 || !!result?.ok || checkingOutside || !!outsideError}
          startIcon={uploading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
          onClick={handleUpload}
          title={alreadyCount > 0 ? `${alreadyCount} row(s) are already in payments and will not be sent` : undefined}
          sx={{ textTransform: 'none', backgroundColor: '#1e40af', '&:hover': { backgroundColor: '#1e3a8a' } }}
        >
          {uploading ? 'Uploading…' : `Upload ${freshRows.length} new transaction${freshRows.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BankStatementUpload;
